import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  ProjectDropdownOption,
  SolutionStory,
  SolutionStoryDirectApprovalCreate,
  SolutionStoryDirectApprovalPrLookup,
  solutionStoriesApi,
} from '../../services/api';
import { getProjectStatusColor } from '../customerSentiment/forms/utils';
import { SEARCE_COLORS } from '../../theme';
import { SolutionStorySlidePreview } from './SolutionStorySlidePreview';
import { splitCsv } from './storyHelpers';
import { STORY_UI } from './storyUiTokens';
import { useSolutionStoryOptions } from './useSolutionStoryOptions';

type Step = 'form' | 'preview';

type DirectApprovalDraft = {
  solution_provided_title: string;
  company_details: string;
  business_context_1: string;
  business_context_2: string;
  business_context_3: string;
  business_context_4: string;
  solution_1: string;
  solution_2: string;
  solution_3: string;
  solution_4: string;
  impact_1: string;
  impact_2: string;
  impact_3: string;
  impact_4: string;
  platform: string;
  tech_stack: string;
  industry: string;
  region: string;
  referencable: string;
  practice: string;
  account_name: string;
};

const emptyDraft = (): DirectApprovalDraft => ({
  solution_provided_title: '',
  company_details: '',
  business_context_1: '',
  business_context_2: '',
  business_context_3: '',
  business_context_4: '',
  solution_1: '',
  solution_2: '',
  solution_3: '',
  solution_4: '',
  impact_1: '',
  impact_2: '',
  impact_3: '',
  impact_4: '',
  platform: '',
  tech_stack: '',
  industry: '',
  region: '',
  referencable: '',
  practice: '',
  account_name: '',
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
    fontFamily: STORY_UI.fontSans,
    backgroundColor: '#fff',
  },
  '& .MuiInputLabel-root': { fontSize: '13px' },
};

function draftToStory(
  draft: DirectApprovalDraft,
  lookup: SolutionStoryDirectApprovalPrLookup,
  prCode: string
): SolutionStory {
  const empty = (v: string) => v.trim() || null;
  return {
    solution_story_id: lookup.next_solution_story_id,
    solution_provided_title: draft.solution_provided_title.trim(),
    business_context_1: empty(draft.business_context_1),
    business_context_2: empty(draft.business_context_2),
    business_context_3: empty(draft.business_context_3),
    business_context_4: empty(draft.business_context_4),
    solution_1: empty(draft.solution_1),
    solution_2: empty(draft.solution_2),
    solution_3: empty(draft.solution_3),
    solution_4: empty(draft.solution_4),
    impact_1: empty(draft.impact_1),
    impact_2: empty(draft.impact_2),
    impact_3: empty(draft.impact_3),
    impact_4: empty(draft.impact_4),
    platform: empty(draft.platform),
    tech_stack: empty(draft.tech_stack),
    industry: empty(draft.industry),
    region: empty(draft.region),
    referencable: empty(draft.referencable),
    company_details: empty(draft.company_details),
    practice: empty(draft.practice),
    pr_code: prCode,
    opportunity_status: lookup.opportunity_status,
    project_status: lookup.project_status,
    account_name: empty(draft.account_name),
    review_status: 'Approved',
    reviewer: null,
  };
}

function draftToPayload(
  draft: DirectApprovalDraft,
  prCode: string,
  createSlidesAndEmail: boolean
): SolutionStoryDirectApprovalCreate {
  const empty = (v: string) => v.trim() || undefined;
  return {
    pr_code: prCode,
    solution_provided_title: draft.solution_provided_title.trim(),
    create_slides_and_email: createSlidesAndEmail,
    company_details: empty(draft.company_details),
    business_context_1: empty(draft.business_context_1),
    business_context_2: empty(draft.business_context_2),
    business_context_3: empty(draft.business_context_3),
    business_context_4: empty(draft.business_context_4),
    solution_1: empty(draft.solution_1),
    solution_2: empty(draft.solution_2),
    solution_3: empty(draft.solution_3),
    solution_4: empty(draft.solution_4),
    impact_1: empty(draft.impact_1),
    impact_2: empty(draft.impact_2),
    impact_3: empty(draft.impact_3),
    impact_4: empty(draft.impact_4),
    platform: empty(draft.platform),
    tech_stack: empty(draft.tech_stack),
    industry: empty(draft.industry),
    region: empty(draft.region),
    referencable: empty(draft.referencable),
    practice: empty(draft.practice),
    account_name: empty(draft.account_name),
  };
}

const pointFields = (
  label: string,
  keys: (keyof DirectApprovalDraft)[],
  draft: DirectApprovalDraft,
  onChange: (key: keyof DirectApprovalDraft, value: string) => void
) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 700,
        color: STORY_UI.inkFaint,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
    </Typography>
    {keys.map((key, idx) => (
      <TextField
        key={String(key)}
        size="small"
        multiline
        minRows={2}
        label={`Point ${idx + 1}`}
        value={draft[key] as string}
        onChange={(e) => onChange(key, e.target.value)}
        sx={fieldSx}
      />
    ))}
  </Box>
);

export const DirectApprovalPanel: React.FC = () => {
  const [step, setStep] = useState<Step>('form');
  const [selectedProject, setSelectedProject] = useState<ProjectDropdownOption | null>(null);
  const [lookup, setLookup] = useState<SolutionStoryDirectApprovalPrLookup | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DirectApprovalDraft>(emptyDraft());
  const [formError, setFormError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [createSlidesAndEmail, setCreateSlidesAndEmail] = useState(false);
  const [slidesUrl, setSlidesUrl] = useState<string | null>(null);
  const [createdStoryId, setCreatedStoryId] = useState<string | null>(null);

  const projectsQuery = useQuery({
    queryKey: ['solution-stories-direct-approval-projects'],
    queryFn: () => solutionStoriesApi.listDirectApprovalProjects(),
    staleTime: 5 * 60 * 1000,
  });

  const { practiceOptions, industryOptions, platformOptions } = useSolutionStoryOptions();

  const projectOptions = projectsQuery.data?.projects ?? [];
  const prCode = selectedProject?.project_code?.trim() || '';

  const previewStory = useMemo(() => {
    if (!lookup || !prCode) return null;
    return draftToStory(draft, lookup, prCode);
  }, [draft, lookup, prCode]);

  const setDraftField = (key: keyof DirectApprovalDraft, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const resetAll = () => {
    setStep('form');
    setSelectedProject(null);
    setLookup(null);
    setLookupError(null);
    setDraft(emptyDraft());
    setFormError(null);
    setApproveError(null);
    setCreateSlidesAndEmail(false);
    setSlidesUrl(null);
    setCreatedStoryId(null);
  };

  const loadProjectContext = async (project: ProjectDropdownOption) => {
    const pc = project.project_code.trim();
    if (!pc) return;
    setLookupLoading(true);
    setLookupError(null);
    setLookup(null);
    try {
      const result = await solutionStoriesApi.lookupDirectApprovalPr(pc);
      setLookup(result);
      setDraft((prev) => ({
        ...prev,
        account_name: project.account || result.account_name || prev.account_name,
        region: prev.region || result.region || project.project_account_bu || '',
      }));
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load project context';
      setLookupError(typeof detail === 'string' ? detail : 'Failed to load project context');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleProjectSelect = (project: ProjectDropdownOption | null) => {
    setSelectedProject(project);
    setLookup(null);
    setLookupError(null);
    if (project) {
      setDraft((prev) => ({
        ...prev,
        account_name: project.account || '',
        region: project.project_account_bu || prev.region,
      }));
      void loadProjectContext(project);
    } else {
      setDraft((prev) => ({
        ...prev,
        account_name: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    if (!selectedProject || !prCode) {
      setFormError('Project code is required');
      return false;
    }
    if (!lookup) {
      setFormError(lookupLoading ? 'Loading project details…' : 'Select a valid project code');
      return false;
    }
    const title = draft.solution_provided_title.trim();
    if (!title) {
      setFormError('Solution provided title is required');
      return false;
    }
    if (title.length > 70) {
      setFormError('Title must be at most 70 characters');
      return false;
    }
    setFormError(null);
    return true;
  };

  const handleContinueToPreview = () => {
    if (!validateForm()) return;
    setStep('preview');
    setApproveError(null);
  };

  const handleApprove = async () => {
    if (!lookup || approving || !prCode) return;
    if (!validateForm()) return;
    setApproving(true);
    setApproveError(null);
    try {
      const res = await solutionStoriesApi.createDirectApprovalStory(
        draftToPayload(draft, prCode, createSlidesAndEmail)
      );
      setCreatedStoryId(res.story.solution_story_id);
      setSlidesUrl(res.presentation_url || null);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to create story';
      setApproveError(typeof detail === 'string' ? detail : 'Failed to create story');
    } finally {
      setApproving(false);
    }
  };

  if (createdStoryId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Story <strong>{createdStoryId}</strong> was approved
          {slidesUrl ? ' and slides were created.' : '.'}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {slidesUrl && (
            <Button
              variant="contained"
              startIcon={<OpenIcon />}
              href={slidesUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: STORY_UI.radiusSm,
                backgroundColor: STORY_UI.accent,
                '&:hover': { backgroundColor: STORY_UI.accentDeep },
              }}
            >
              Open Slides
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={resetAll}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: STORY_UI.radiusSm,
              borderColor: STORY_UI.line,
              color: STORY_UI.inkSoft,
            }}
          >
            Create another
          </Button>
        </Box>
      </Box>
    );
  }

  if (step === 'preview' && previewStory) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box
          sx={{
            borderRadius: STORY_UI.radiusMd,
            border: `1px solid ${STORY_UI.line}`,
            overflow: 'hidden',
            backgroundColor: STORY_UI.surface,
          }}
        >
          <SolutionStorySlidePreview story={previewStory} showExecutiveSummary={false} />
        </Box>

        {approveError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {approveError}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={createSlidesAndEmail}
                onChange={(e) => setCreateSlidesAndEmail(e.target.checked)}
                disabled={approving}
                sx={{
                  color: STORY_UI.inkFaint,
                  '&.Mui-checked': { color: STORY_UI.accent },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: STORY_UI.inkSoft }}>
                Create Google Slides and send notification email
              </Typography>
            }
          />
          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <Button
            disabled={approving}
            onClick={() => setStep('form')}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: STORY_UI.radiusSm,
              color: STORY_UI.inkSoft,
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            disabled={approving}
            startIcon={approving ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={handleApprove}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: STORY_UI.radiusSm,
              backgroundColor: STORY_UI.accent,
              '&:hover': { backgroundColor: STORY_UI.accentDeep },
            }}
          >
            Approve
          </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        p: { xs: 2, sm: 3 },
        borderRadius: STORY_UI.radiusMd,
        border: `1px solid ${STORY_UI.line}`,
        backgroundColor: STORY_UI.surface,
      }}
    >
      <Typography
        sx={{
          fontFamily: STORY_UI.fontDisplay,
          fontWeight: 600,
          fontSize: '16px',
          color: STORY_UI.ink,
        }}
      >
        Project selection
      </Typography>

      {projectsQuery.error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Failed to load projects. Please refresh and try again.
        </Alert>
      )}

      <Autocomplete
        options={projectOptions}
        getOptionLabel={(option) => option.display_label}
        value={selectedProject}
        onChange={(_, newValue) => handleProjectSelect(newValue)}
        loading={projectsQuery.isLoading}
        isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Project Code"
            required
            helperText="Select a project from the tracker"
            sx={fieldSx}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {projectsQuery.isLoading || lookupLoading ? (
                    <CircularProgress size={18} sx={{ color: STORY_UI.accent }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.project_code}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
                  {option.project_code}
                </Typography>
                {option.status && (
                  <Chip
                    label={option.status}
                    size="small"
                    sx={{
                      backgroundColor: getProjectStatusColor(option.status),
                      color: SEARCE_COLORS.white,
                      fontSize: '0.7rem',
                      height: 20,
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary }}>
                {option.project_name}
              </Typography>
            </Box>
          </Box>
        )}
      />

      {lookupError && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {lookupError}
        </Alert>
      )}

      {lookup && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`Story ID: ${lookup.next_solution_story_id}`}
            sx={{ fontFamily: STORY_UI.fontMono, fontSize: '12px' }}
          />
          <Chip label={`Opportunity: ${lookup.opportunity_status || selectedProject?.project_opportunity_stage || '—'}`} />
          <Chip label={`Project: ${lookup.project_status || selectedProject?.status || '—'}`} />
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <TextField
          size="small"
          label="Account name"
          value={draft.account_name}
          disabled
          helperText={selectedProject ? 'Auto-filled from project' : 'Select a project to auto-fill'}
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="Opportunity"
          value={selectedProject?.opportunity_name || ''}
          disabled
          helperText={selectedProject ? 'Auto-filled from project' : 'Select a project to auto-fill'}
          sx={fieldSx}
        />
      </Box>

      <Typography
        sx={{
          fontFamily: STORY_UI.fontDisplay,
          fontWeight: 600,
          fontSize: '16px',
          color: STORY_UI.ink,
          pt: 0.5,
        }}
      >
        Solution story details
      </Typography>

      <TextField
        size="small"
        required
        label="Solution provided title"
        value={draft.solution_provided_title}
        onChange={(e) => setDraftField('solution_provided_title', e.target.value)}
        helperText={`${draft.solution_provided_title.length}/70`}
        inputProps={{ maxLength: 70 }}
        sx={fieldSx}
      />

      <TextField
        size="small"
        multiline
        minRows={3}
        label="Company details"
        value={draft.company_details}
        onChange={(e) => setDraftField('company_details', e.target.value)}
        sx={fieldSx}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 2,
        }}
      >
        {pointFields(
          'Business Context',
          ['business_context_1', 'business_context_2', 'business_context_3', 'business_context_4'],
          draft,
          setDraftField
        )}
        {pointFields(
          'Solution',
          ['solution_1', 'solution_2', 'solution_3', 'solution_4'],
          draft,
          setDraftField
        )}
        {pointFields(
          'Impact',
          ['impact_1', 'impact_2', 'impact_3', 'impact_4'],
          draft,
          setDraftField
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="direct-platform-label">Platform</InputLabel>
          <Select
            labelId="direct-platform-label"
            label="Platform"
            multiple
            value={splitCsv(draft.platform)}
            onChange={(e) => {
              const value = e.target.value;
              const next = (typeof value === 'string' ? value.split(',') : value).slice(0, 3);
              setDraftField('platform', next.join(', '));
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {platformOptions.map((p) => {
              const selected = splitCsv(draft.platform);
              const isSelected = selected.includes(p);
              const atMax = selected.length >= 3;
              return (
                <MenuItem key={p} value={p} disabled={!isSelected && atMax}>
                  {p}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Tech stack"
          value={draft.tech_stack}
          onChange={(e) => setDraftField('tech_stack', e.target.value)}
          helperText="Comma-separated if multiple"
          sx={fieldSx}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="direct-industry-label">Industry</InputLabel>
          <Select
            labelId="direct-industry-label"
            label="Industry"
            value={draft.industry}
            onChange={(e) => setDraftField('industry', e.target.value)}
          >
            <MenuItem value="">
              <em>Select industry</em>
            </MenuItem>
            {industryOptions.map((industry) => (
              <MenuItem key={industry} value={industry}>
                {industry}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Region"
          value={draft.region}
          onChange={(e) => setDraftField('region', e.target.value)}
          helperText={selectedProject ? 'Pre-filled from project; editable' : undefined}
          sx={fieldSx}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="direct-practice-label">Practice</InputLabel>
          <Select
            labelId="direct-practice-label"
            label="Practice"
            value={draft.practice}
            onChange={(e) => setDraftField('practice', e.target.value)}
          >
            <MenuItem value="">
              <em>Select practice</em>
            </MenuItem>
            {practiceOptions.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="direct-ref-label">Referencable</InputLabel>
          <Select
            labelId="direct-ref-label"
            label="Referencable"
            value={draft.referencable}
            onChange={(e) => setDraftField('referencable', e.target.value)}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            <MenuItem value="R">R (Referencable)</MenuItem>
            <MenuItem value="NR">NR (Non-referencable)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {formError && (
        <Typography sx={{ fontSize: '12.5px', color: '#c00000' }}>{formError}</Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disabled={lookupLoading}
          onClick={handleContinueToPreview}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: STORY_UI.radiusSm,
            backgroundColor: STORY_UI.accent,
            '&:hover': { backgroundColor: STORY_UI.accentDeep },
          }}
        >
          Continue to preview
        </Button>
      </Box>
    </Box>
  );
};
